import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import DetailsHoverPopOverList from "../../../../../../../components/DetailsHoverPopOverList";
import Flex from "../../../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import { BILLING_TABLE_ROW_HEIGHT } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useGetPatientPaymentV2 } from "../../../../../../../queries/billings/payments/use-get-patient-payment-v2";
import { NonNullableDate } from "../../../../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  filterActionCol,
  formatMoney,
  // getFormattedId, // used by the commented-out Payment ID column
  getValueFromObj,
  roleFilter,
} from "../../../../../../../utils/utils";
import ViewPatientPayment from "../../../../billings/payments/ViewPatientPayment";

const PatientInsurancePayment = () => {
  const { patientId } = useParams();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const { hasReadOnly } = useRoleController("Financials");
  const { hasView: hasViewInsurancePayments } =
    useRoleController("Insurance Payments");

  const viewPatientPaymentDisclosureHost = useDisclosure();

  const { data: patientPayment, isFetching } = useGetPatientPaymentV2({
    pagination,
    gridSorting,
    typeOfPayment: "INSURANCE",
    patientUuid: patientId ?? undefined,
  });

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "visitIds",
        flex: 0.3,
        minWidth: 100,
        headerName: "Visit ID",
        renderCell: ({ row }) => {
          const visitIdsObj = row?.visitIds || {};
          const visitIdKeys = Object.keys(visitIdsObj);
          return <DetailsHoverPopOverList data={visitIdKeys} />;
        },
      },
      {
        field: "claimIds",
        flex: 0.4,
        minWidth: 50,
        disableColumnMenu: true,
        headerName: "Claim ID",
        renderCell: ({ row }) => {
          const claimIdsObj = row?.claimIds || {};
          const claimIdKeys = Object.keys(claimIdsObj);
          return <DetailsHoverPopOverList data={claimIdKeys} />;
        },
      },
      {
        field: "payerName",
        flex: 0.6,
        minWidth: 100,
        headerName: "Payer Name",
        renderCell: ({ row }) => {
          return (
            <RenderWithTooltip value={row?.insurancePayer?.payerName ?? ""} />
          );
        },
      },
      {
        field: "providerName",
        flex: 0.5,
        minWidth: 150,
        headerName: "Provider",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Flex gap={"10px"}>
              <Box>
                <Text isTabletext marginBottom={"1px"}>
                  {!isEmpty(row?.billingProvider)
                    ? `${getValueFromObj(row?.billingProvider)}`
                    : ""}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      // Payment ID column dropped from the Insurance Payment sequence.
      // {
      //   field: "uuid",
      //   flex: 0.4,
      //   minWidth: 50,
      //   disableColumnMenu: true,
      //   headerName: "Payment ID",
      //   renderCell: ({ row }) => (row?.uuid ? getFormattedId(row?.uuid) : "-"),
      // },
      {
        field: "billedAmount",
        flex: 0.3,
        minWidth: 110,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Billed Amount",
        renderCell: ({ row }) => formatMoney(row?.billedAmount),
      },
      {
        field: "amount",
        flex: 0.3,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Paid Amount",
        renderCell: ({ row }) => formatMoney(row?.amount),
      },
      {
        field: "transactionDate",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Transaction Date",
        renderCell: ({ row }) => NonNullableDate(row?.paymentDate),
      },
      {
        field: "paymentMethod",
        flex: 0.5,
        minWidth: 100,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Payment Mode",
        renderCell: ({ row }) => {
          return (
            <Text isTabletext>
              {row?.paymentMode === "BANK"
                ? (row?.bankPaymentMode ?? "-")
                : row?.paymentMode === "ONLINE"
                  ? "CARD"
                  : (row?.paymentMode ?? "-")}
            </Text>
          );
        },
      },
      {
        field: "transactionId",
        flex: 0.4,
        minWidth: 120,
        headerName: "Tx ID/Cheque ID",
        renderCell: ({ row }) => (
          <RenderWithTooltip
            value={row?.transactionId || row?.chequeId || ""}
          />
        ),
      },
      {
        field: "appliedAmount",
        flex: 0.3,
        minWidth: 115,
        disableColumnMenu: true,
        headerName: "Applied Amount",
        renderCell: ({ row }) => formatMoney(row?.appliedAmount),
      },
      {
        field: "unappliedAmount",
        flex: 0.3,
        minWidth: 125,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Unapplied Amount",
        renderCell: ({ row }) => formatMoney(row?.unappliedAmount),
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "view", label: "View Payment" },
                  // Apply Payment disabled, matching the Billing → Payments listing.
                  // { value: "apply", label: "Apply Payment" },
                ],
                compWith: {
                  view: hasViewInsurancePayments,
                  // apply: hasUpdateInsurancePayments,
                },
              })}
              onActionClick={(v) => {
                if (v === "view") {
                  viewPatientPaymentDisclosureHost.open(row?.uuid);
                }
              }}
            />
          );
        },
      },
    ],
    ["transactionDate", "amount"]
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ViewPatientPayment
        title="View Payment"
        disclosureHost={viewPatientPaymentDisclosureHost}
      />

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          loading={isFetching}
          rows={patientPayment?.data?.content ?? []}
          rowCount={patientPayment?.data?.page?.totalElements ?? 10}
          getRowId={(row) => row?.uuid}
          columns={filterActionCol(COLUMNS, hasReadOnly)}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          sortModel={gridSorting}
          rowHeight={BILLING_TABLE_ROW_HEIGHT}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientInsurancePayment;
